import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid, Theme } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useTheme } from "@mui/styles";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v1 } from "uuid";
// Flight Chips
import { IFlight, IFlightType, IGroundEvent, INewGroundEvent } from "@vmosolutions/oc-interfaces";
import { IBuildPayload, IUpdateTableEvents } from "./BuildStatus/BuildStatus";
import { EventTypes } from "./FlightBuilder";
import { getInitialValues, validateGroundEvent } from "./Forms/GroundEventRowEdit";
import { GroundEventRows } from "./Forms/GroundEventRows";

interface IGroundEventsProps {
  id: string;
  path: string[];
  eventType: { value: EventTypes; label: string };
  setFocusOnEvent?: (event: IFlight | IGroundEvent) => void;
  sendBuildStatus?: (buildPayload: IBuildPayload) => void;
  updateTableEvents: IUpdateTableEvents;
  defaultGroundEventType?: IFlightType;
}

const FlightBuilderFormStyles = (theme: Theme): any => ({
  height: "fit-content",
  padding: "10px 7px",
  minWidth: 1120,
  "& .formContainer": {
    "& .pairing_creator_info_card, & .flights_form_card, & .recurring_flights_form_card, & .ground_events_form_card": {
      flexGrow: 1,
      boxShadow: theme.shadows[5],
      margin: "8px",
      backgroundColor: theme.custom.timeline.contextListBackground,
    },
    "& .time_mode_selector": {
      justifyContent: "flex-start",
    },
  },
  "& .tableContainer": {
    borderRadius: "5px",
    marginLeft: "8px",
    marginTop: "6px",
    flexGrow: 1,
    boxShadow: theme.shadows[5],
    backgroundColor: theme.custom.timeline.contextListBackground,
  },
});

// Strip out any invalid events before sending to build status tool.
const showOnlyValidEvents = (flights: INewGroundEvent[]): INewGroundEvent[] => {
  return flights.filter((f) => !!f?._id.includes("temp") && Object.entries(validateGroundEvent(f))?.length === 0);
};

/**
 * CREATE GROUND EVENT COMPONENT
 */
export const GroundEvents = (props: IGroundEventsProps): JSX.Element => {
  const theme: Theme = useTheme();
  const { id, eventType, sendBuildStatus, updateTableEvents, defaultGroundEventType } = props;

  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(true);
  const [quickGroundEvents, setQuickGroundEvents] = useState<INewGroundEvent[]>([
    getInitialValues(defaultGroundEventType),
  ]);

  const validEvents = useMemo(() => showOnlyValidEvents(quickGroundEvents), [quickGroundEvents]);

  // As the status sends back info on successfully updated events change their temp ids to actuals.
  useEffect(() => {
    if (
      !updateTableEvents ||
      (updateTableEvents.hasOwnProperty("cleanOut") && Object.keys(updateTableEvents)?.length === 1)
    ) {
      return;
    }
    // If resetAll is passed, reset all ids back to temps.
    if (updateTableEvents.resetAll) {
      setQuickGroundEvents((current) => [...current].map((f) => ({ ...f, _id: `temp-${v1()}` })));
    } else {
      setQuickGroundEvents((current) => {
        if (!updateTableEvents?.cleanOut) {
          // event successfully built, leave in table but replace it's temp id.
          return current.map((event) => {
            if (updateTableEvents[event._id]) {
              return { ...event, _id: updateTableEvents[event._id] };
            }
            return event;
          });
        } else {
          // REMOVE any events that have successfully been build.
          return current.filter((event) => !updateTableEvents[event._id]);
        }
      });
    }
  }, [updateTableEvents]);

  //TODO Add a modal to this call so that users can't accidentally remove an event.
  /**
   * Remove a selected leg
   */
  const removeLeg = useCallback(
    (id: string): void => {
      const updatedEvents = [...quickGroundEvents];
      const findIndex = updatedEvents?.findIndex((f) => f?._id == id);
      if (findIndex > -1) {
        updatedEvents.splice(findIndex, 1);
        // updatedEvents.sort((a, b) => (a?.scheduledOut as number) - (b?.scheduledOut as number));
        setQuickGroundEvents(updatedEvents);
      }
    },
    [quickGroundEvents]
  );

  /**
   * Add a New leg
   */
  const addLeg = useCallback((): void => {
    const lastEvent = quickGroundEvents[quickGroundEvents?.length - 1];
    const newLeg = {
      ...getInitialValues(defaultGroundEventType),
      ...{
        location: lastEvent?.location, // prefill
        eventType: lastEvent?.eventType, // prefill
        scheduledStart: lastEvent?.scheduledEnd, // prefill scheduled out with last flights scheduled in.
        aircraft: lastEvent?.aircraft, // prefill
      },
    };
    setQuickGroundEvents([...quickGroundEvents, newLeg]);
  }, [defaultGroundEventType, quickGroundEvents]);

  /**
   * Function fired off if user updated a selected leg.
   */
  const updateLeg = useCallback(
    (formValues: INewGroundEvent): void => {
      const updatedEvents = [...quickGroundEvents];
      const findIndex = updatedEvents?.findIndex((f) => f?._id == formValues?._id);
      if (findIndex > -1) {
        updatedEvents[findIndex] = formValues;
        // updatedFlights?.sort((a, b) => (a?.scheduledOut as number) - (b?.scheduledOut as number));
        setQuickGroundEvents(updatedEvents);
      }
    },
    [quickGroundEvents]
  );

  // Update Build Status payload and sent to Build Status component.
  // Create deep close of the ground events array (avoids mutation by the status tool)
  useEffect(() => {
    const events = _.cloneDeep(validEvents);
    sendBuildStatus({ eventPayload: events });
  }, [validEvents, sendBuildStatus]);

  // Reset all changes to template legs
  const resetAllChangesToLegs = useCallback(() => {
    setQuickGroundEvents([getInitialValues(defaultGroundEventType)]);
  }, [defaultGroundEventType]);

  return (
    <Grid container sx={FlightBuilderFormStyles(theme)} direction="column" wrap="nowrap">
      <Grid item className={"formContainer"}>
        <GroundEventRows
          mosaicId={id}
          legs={quickGroundEvents}
          legsAreDirty={validEvents.length > 0}
          resetAllChangesToLegs={resetAllChangesToLegs}
          onSubmit={updateLeg}
          addLeg={addLeg}
          removeLeg={removeLeg}
          eventType={eventType.value}
          displayAsUTC={displayAsUTC}
          setDisplayAsUTC={setDisplayAsUTC}
        />
      </Grid>

      <Accordion className={"tableContainer"} defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {`Pending Ground Events (${validEvents?.length})`}
        </AccordionSummary>
      </Accordion>
    </Grid>
  );
};
